import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import Footer from '../components/Footer';
import PublicNav from '../components/PublicNav';
import { Button } from '../components/ui';
import { blogService, BlogPost } from '../services/blogService';
import { getUploadUrl } from '../utils/url';

const PAGE_TITLE = 'Blog — Solofix';
const PAGE_DESCRIPTION =
  'Verhalen, tips en inspiratie over samen naar festivals en evenementen gaan. Lees de Solofix blog en ga nooit meer alleen.';

const formatDate = (value?: string | null) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const BlogListPage: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    document.title = PAGE_TITLE;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    setMeta('description', PAGE_DESCRIPTION);
    setMeta('og:title', PAGE_TITLE, true);
    setMeta('og:description', PAGE_DESCRIPTION, true);
    setMeta('og:type', 'website', true);

    return () => {
      document.title = 'Solofix';
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    blogService
      .listPublic(page, 12)
      .then((result) => {
        if (!active) return;
        setPosts(result.posts);
        setTotalPages(result.pagination.totalPages);
      })
      .catch((error) => {
        console.error('Error loading blog posts:', error);
        if (active) setPosts([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page]);

  return (
    <div className="min-h-screen">
      <PublicNav />
      {/* Header */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-4">
        <div className="sf-eyebrow mb-3">// blog</div>
        <h1 className="sf-h1 mb-4">De Solofix blog</h1>
        <p className="text-fg-muted text-lg max-w-2xl">{PAGE_DESCRIPTION}</p>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-neon-pink" />
          </div>
        ) : posts.length === 0 ? (
          <div className="sf-card p-10 text-center text-fg-muted">
            Er zijn nog geen blogberichten. Kom snel terug!
          </div>
        ) : (
          <>
            <div className="grid gap-6 sm:grid-cols-2">
              {posts.map((post) => {
                const cover = getUploadUrl(post.coverImage);
                return (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="sf-card group overflow-hidden transition-colors hover:bg-white/[.05] flex flex-col"
                  >
                    {cover && (
                      <div className="aspect-[16/9] w-full overflow-hidden">
                        <img
                          src={cover}
                          alt={post.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    )}
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center text-xs text-fg-muted mb-3">
                        <CalendarDays className="w-3.5 h-3.5 mr-1.5 text-neon-cyan" />
                        {formatDate(post.publishedAt || post.createdAt)}
                      </div>
                      <h2 className="text-xl font-bold tracking-tight text-fg-bright mb-2 leading-snug">
                        {post.title}
                      </h2>
                      {post.excerpt && (
                        <p className="text-fg-muted text-sm leading-relaxed line-clamp-3">
                          {post.excerpt}
                        </p>
                      )}
                      <span className="mt-4 inline-flex items-center text-sm font-medium text-neon-cyan">
                        Lees verder →
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-10">
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  disabled={page <= 1}
                >
                  Vorige
                </Button>
                <span className="text-sm text-fg-muted">
                  Pagina {page} van {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  disabled={page >= totalPages}
                >
                  Volgende
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BlogListPage;
